import { useEffect, useMemo, useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Check, Loader2, Plus, Search, X } from 'lucide-react'
import type { ProcessStepConnection } from '@/api/processStepsApi'
import {
  MATERIAL_TYPE_LABELS,
  useProcessMaterials,
  type MaterialType,
} from '@/components/process/materials'
import { useUpsertProcessStep } from '@/hooks/useProcessSteps'
import { FEATURES } from '@/lib/featureFlags'
import { useFeatureFlag } from '@/providers/FeatureFlagsProvider'
import { FeatureDisabledNote } from '@/components/auth/FeatureDisabledNote'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

// ─── Local editable row ──────────────────────────────────────────────────────
//
// One inventory requirement being edited. Seeded from the loaded connection and
// diffed against it to drive the dirty/Save state. Qty is held as a string so
// partial input doesn't fight the controlled input.

interface DraftRow {
  consumableId: string
  /** Denormalized Material ID at authoring time. */
  itemLabel: string
  qty: string
}

function toDraft(r: { consumableId: string; itemLabel: string | null; qtyRequired: number }): DraftRow {
  return {
    consumableId: r.consumableId,
    itemLabel: r.itemLabel ?? r.consumableId,
    qty: String(r.qtyRequired),
  }
}

/** Parse a qty input; NaN / non-positive collapse to 1. */
function qty(value: string): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 1
}

function serialize(rows: DraftRow[]): string {
  return JSON.stringify(rows.map((r) => ({ consumableId: r.consumableId, qty: qty(r.qty) })))
}

// ─── Material picker dialog ──────────────────────────────────────────────────
//
// Browsable catalog of materials grouped by type, searchable by Material ID /
// detail, with type filter chips; added materials stay visible with a green
// check (click to remove) and the list scrolls for large catalogs.

function MaterialPickerDialog({
  open,
  onClose,
  addedIds,
  onAdd,
  onRemove,
}: {
  open: boolean
  onClose: () => void
  addedIds: Set<string>
  onAdd: (materialId: string) => void
  onRemove: (materialId: string) => void
}) {
  const [query, setQuery] = useState('')
  const [type, setType] = useState<MaterialType | 'all'>('all')
  const { materials, isLoading } = useProcessMaterials()

  // Reset filters each time the picker opens.
  useEffect(() => {
    if (open) {
      setQuery('')
      setType('all')
    }
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return materials.filter((m) => {
      if (type !== 'all' && m.type !== type) return false
      if (!q) return true
      return (
        m.label.toLowerCase().includes(q) ||
        m.detail.toLowerCase().includes(q) ||
        MATERIAL_TYPE_LABELS[m.type].toLowerCase().includes(q)
      )
    })
  }, [query, type, materials])

  // Group the filtered materials under their type for section headers.
  const sections = useMemo(() => {
    const order: MaterialType[] = ['consumable', 'reagent', 'kit']
    return order
      .map((t) => ({
        type: t,
        label: MATERIAL_TYPE_LABELS[t],
        materials: filtered
          .filter((m) => m.type === t)
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .filter((s) => s.materials.length > 0)
  }, [filtered])

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className='flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl'>
        <DialogHeader className='border-b border-gray-100 px-6 pb-4 pt-5'>
          <DialogTitle>Add inventory</DialogTitle>
          <DialogDescription>
            Search by Material ID or type — click <Plus className='inline h-3 w-3' /> to add a
            material to this process step.
          </DialogDescription>
        </DialogHeader>

        {/* Search + type filter */}
        <div className='space-y-2.5 border-b border-gray-100 px-6 py-3'>
          <div className='relative'>
            <Search className='pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search material ID, name or type…'
              className='h-9 pl-8'
            />
          </div>
          <div className='flex flex-wrap gap-1.5'>
            {(['all', 'consumable', 'reagent', 'kit'] as const).map((t) => (
              <button
                key={t}
                type='button'
                onClick={() => setType(t)}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                  type === t
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 bg-white text-muted-foreground hover:bg-gray-50',
                )}
              >
                {t === 'all' ? 'All types' : MATERIAL_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Material list */}
        <div className='min-h-0 flex-1 overflow-y-auto px-6 py-3'>
          {isLoading && (
            <p className='flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading materials…
            </p>
          )}
          {!isLoading && sections.length === 0 && (
            <p className='py-8 text-center text-sm text-muted-foreground'>
              No materials match “{query}”.
            </p>
          )}
          {sections.map((section) => (
            <div key={section.type} className='mb-3'>
              <p className='sticky top-0 z-10 -mx-1 bg-background px-1 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
                {section.label}
                <span className='ml-1.5 font-normal normal-case text-gray-300'>
                  ({section.materials.length})
                </span>
              </p>
              <ul className='space-y-1'>
                {section.materials.map((m) => {
                  const added = addedIds.has(m.id)
                  return (
                    <li
                      key={m.id}
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-lg border px-3 py-2 transition-colors',
                        added ? 'border-green-200 bg-green-50/60' : 'border-gray-200 bg-white',
                      )}
                    >
                      <div className='min-w-0'>
                        <p className='truncate text-sm font-medium text-foreground'>{m.label}</p>
                        <p className='truncate text-xs text-muted-foreground'>{m.detail}</p>
                      </div>
                      {added ? (
                        <button
                          type='button'
                          onClick={() => onRemove(m.id)}
                          title='Added — click to remove'
                          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600 text-white transition-colors hover:bg-gray-400'
                        >
                          <Check className='h-4 w-4' strokeWidth={3} />
                        </button>
                      ) : (
                        <button
                          type='button'
                          onClick={() => onAdd(m.id)}
                          title='Add to process step'
                          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors hover:border-primary hover:bg-primary hover:text-white'
                        >
                          <Plus className='h-4 w-4' />
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        <DialogFooter className='flex-row items-center border-t border-gray-100 px-6 py-3 sm:justify-between'>
          <p className='text-xs text-muted-foreground'>
            {addedIds.size} of {materials.length} added
          </p>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Tab ─────────────────────────────────────────────────────────────────────

// Per-user column layout. Material ID is locked (it identifies the line) and so
// is the remove control. See src/components/table/index.ts for the pattern.
const INVENTORY_REQ_TABLE_ID = 'process-inventory-required'

const INVENTORY_REQ_COLUMNS: TableColumnDef[] = [
  { id: 'materialId', header: 'Material ID', locked: true },
  { id: 'type', header: 'Type' },
  { id: 'qty', header: 'Qty' },
  { id: 'actions', header: 'Remove', locked: true },
]

const INVENTORY_REQ_HEAD_CLASSES: Record<string, string | undefined> = {
  qty: 'text-right',
  actions: 'w-12',
}

export function InventoryRequiredTab({
  fieldId,
  connection,
}: {
  fieldId: string
  connection: ProcessStepConnection | null
}) {
  const cols = useTableColumns(INVENTORY_REQ_TABLE_ID, INVENTORY_REQ_COLUMNS)
  const inventoryOn = useFeatureFlag(FEATURES.inventory)
  const upsert = useUpsertProcessStep()
  const { materialById } = useProcessMaterials()

  // Seed local state from the connection; re-seed when the connection (or field) changes.
  const [rows, setRows] = useState<DraftRow[]>([])
  const [savedAt, setSavedAt] = useState<number | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const baseline = useMemo(
    () => (connection?.inventoryRequirements ?? []).map(toDraft),
    [connection],
  )

  useEffect(() => {
    setRows(baseline)
    setSavedAt(null)
  }, [baseline, fieldId])

  const dirty = serialize(rows) !== serialize(baseline)

  // Material ids already added as requirement rows — drives the picker's added state.
  const addedMaterialIds = useMemo(() => new Set(rows.map((r) => r.consumableId)), [rows])

  const addMaterial = (materialId: string) => {
    const material = materialById.get(materialId)
    if (!material || rows.some((r) => r.consumableId === materialId)) return
    setRows((prev) => [
      ...prev,
      { consumableId: material.id, itemLabel: material.label, qty: '1' },
    ])
    setSavedAt(null)
  }

  const patchQty = (index: number, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, qty: value } : r)))
    setSavedAt(null)
  }

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index))
    setSavedAt(null)
  }

  const removeMaterial = (materialId: string) => {
    setRows((prev) => prev.filter((r) => r.consumableId !== materialId))
    setSavedAt(null)
  }

  const save = () => {
    // Inventory only. The other groups are OMITTED, which the endpoint now
    // reads as "leave them alone" — this tab used to have to echo equipment and
    // staffing back on every save, and its echo dropped `prepHrs`, so saving a
    // material reset the equipment prep delay to zero.
    upsert.mutate(
      {
        fieldId,
        payload: {
          inventoryRequirements: rows.map((r, i) => ({
            consumableId: r.consumableId,
            itemLabel: r.itemLabel,
            qtyRequired: qty(r.qty),
            order: i,
          })),
        },
      },
      { onSuccess: () => setSavedAt(Date.now()) },
    )
  }

  // Same reasoning as EquipmentRequirementTab: the materials master is the only
  // thing this tab picks from, so with inventory off "Add inventory" opens an
  // empty catalog and saved rows fall back to raw consumable ids.
  if (!inventoryOn) {
    return (
      <div className='px-5 py-5'>
        <FeatureDisabledNote label='Inventory' />
      </div>
    )
  }

  return (
    <div className='space-y-4 px-5 py-5'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <Button variant='outline' onClick={() => setPickerOpen(true)}>
            <Plus className='mr-1.5 h-4 w-4' />
            Add inventory
          </Button>
          {rows.length > 0 && (
            <span className='text-xs text-muted-foreground'>{rows.length} added</span>
          )}
        </div>
        <div className='flex items-center gap-3'>
          {savedAt && !dirty && (
            <span className='flex items-center gap-1 text-xs text-green-600'>
              <Check className='h-3.5 w-3.5' /> Saved
            </span>
          )}
          <Button onClick={save} disabled={!dirty || upsert.isPending}>
            {upsert.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Save
          </Button>
        </div>
      </div>

      {upsert.isError && (
        <p className='text-sm text-destructive'>
          {upsert.error instanceof Error ? upsert.error.message : 'Could not save inventory.'}
        </p>
      )}

      {/* Added materials as an editable table */}
      {rows.length === 0 ? (
        <p className='rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-muted-foreground'>
          No inventory added yet — click “Add inventory” to pick materials.
        </p>
      ) : (
        <>
        <div className='overflow-x-auto rounded-xl border border-gray-200'>
          <Table>
            <TableHeader>
              <TableRow>
                {cols.visible.map(({ def }) => (
                  <ResizableTableHead
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={INVENTORY_REQ_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'actions'}
                  >
                    {def.header}
                  </ResizableTableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, i) => {
                const material = materialById.get(row.consumableId)
                return (
                  <TableRow key={row.consumableId}>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        materialId: (
                          <TableCell className='font-medium'>
                            {material?.label ?? row.itemLabel}
                            {material?.detail && (
                              <span className='ml-2 text-xs font-normal text-muted-foreground'>
                                {material.detail}
                              </span>
                            )}
                          </TableCell>
                        ),
                        type: (
                          <TableCell className='text-muted-foreground'>
                            {material ? MATERIAL_TYPE_LABELS[material.type] : '—'}
                          </TableCell>
                        ),
                        qty: (
                          <TableCell className='text-right'>
                            <input
                              type='number'
                              inputMode='numeric'
                              aria-label={`Quantity for ${material?.label ?? row.itemLabel}`}
                              min={1}
                              step='1'
                              value={row.qty}
                              onChange={(e) => patchQty(i, e.target.value)}
                              className='h-8 w-20 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            />
                          </TableCell>
                        ),
                        actions: (
                          <TableCell>
                            <button
                              type='button'
                              aria-label='Remove material'
                              title='Remove material'
                              onClick={() => removeRow(i)}
                              className='flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-600 hover:text-white'
                            >
                              <X className='h-4 w-4' strokeWidth={2.5} />
                            </button>
                          </TableCell>
                        ),
                      }}
                    />
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
        </>
      )}

      <MaterialPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        addedIds={addedMaterialIds}
        onAdd={addMaterial}
        onRemove={removeMaterial}
      />
    </div>
  )
}
